// Contracts — work material-eval reason MANIFEST (ADR-0030). The single source of
// truth for the reason vocabulary shared by the eval engine, the result modal,
// the history/notifications surfaces, and the AI assistant. Human copy lives in
// the web i18n as `earn.reason.<key>.t` (factual title) + `.h` (coaching hint);
// this file owns the keys + their metadata (priority + hard-fail).
import { z } from 'zod';

// Canonical reason keys. `ok` = clean pass; `low_confidence`/`eval_unavailable`
// are neutral "under review" states (not a mistake); the rest are shootable
// mistakes. Add a key here + its `.t`/`.h` copy in ALL langs together.
export const WORK_EVAL_REASONS = [
  'ok',
  'low_confidence',
  'eval_unavailable',
  'blurry',
  'not_in_frame',
  'multiple',
  'people',
  'off_language',
  'not_a_card',
  'codes_outside',
  'screen_photo',
  'store_rack',
] as const;
export type WorkEvalReason = (typeof WORK_EVAL_REASONS)[number];

// priority: how prominently to surface (higher first / more visibly). Key
// mistakes the customer cares most about (stray codes, multiple cards, a screen
// re-shot) are 3. `hard`: a high-confidence hit REJECTS (vs neutral review-only).
export interface WorkReasonMeta {
  priority: 0 | 1 | 2 | 3;
  hard: boolean;
  neutral?: boolean; // true → "under review", not a mistake (no coaching hint)
}

// `hard: true`  → a high-confidence hit AUTO-REJECTS (reliable + unambiguous).
// `hard: false` → NEVER auto-rejects; always routes to needs_qa (a human decides).
// multiple/not_in_frame/people are QA-only: on the first-1000 backtest the model
// over-fired them (busy product backgrounds read as "multiple"; a card fully in
// frame beside a stray label read as "not_in_frame"; posters/packaging read as
// "people"). Until a focused validator confirms them (ADR-0030 cascade) they only
// flag for QA. codes_outside stays hard — it was accurate AND is the true reason
// behind many mislabeled not_in_frame cases (stray shipping-label barcodes).
//
// CASCADE (ADR-0030): a flagged frame gets a focused stage-2 scene validator that
// COUNTS cards and detects a retail display. That validator is what makes
// `multiple`/`codes_outside` reliable enough to auto-reject (confirmed, not the
// noisy stage-1 guess) and introduces `store_rack`: exactly one genuine card, but
// photographed in-store among products (ambiguous ownership) → QA + coaching to
// shoot the card alone on a plain surface. `store_rack` is soft (human decides).
export const REASON_META: Record<WorkEvalReason, WorkReasonMeta> = {
  codes_outside: { priority: 3, hard: true },
  screen_photo: { priority: 3, hard: true },
  blurry: { priority: 2, hard: true },
  off_language: { priority: 1, hard: true },
  not_a_card: { priority: 1, hard: true },
  multiple: { priority: 3, hard: true },
  store_rack: { priority: 2, hard: false },
  not_in_frame: { priority: 2, hard: false },
  people: { priority: 2, hard: false },
  low_confidence: { priority: 0, hard: false, neutral: true },
  eval_unavailable: { priority: 0, hard: false, neutral: true },
  ok: { priority: 0, hard: false, neutral: true },
};

// Order a set of reasons for display: highest priority first, stable within.
export function orderReasons(reasons: string[]): string[] {
  const known = reasons.filter((r): r is WorkEvalReason => r in REASON_META);
  return known.slice().sort((a, b) => REASON_META[b].priority - REASON_META[a].priority);
}

// Wire schema stays permissive (a string) so a NEW reason emitted by the worker
// never breaks web parse before the web ships its copy (parse-don't-validate).
// The manifest above is the authoritative key list; the UI falls back to a
// generic line for an unknown key.
export const WorkEvalReasonSchema = z.string();

// ─── Backtest (ADR-0030): rehearse the eval over the existing pool ────────────
// POST /api/work/_admin/evalBacktest (X-Admin-Secret). Runs dryRunCardEval over N
// random frames from the last `pool` uploads of a category, logs each to
// work_eval_log (run_context='backtest'), and returns per-frame verdicts + cost.
export const WorkEvalBacktestReqSchema = z.object({
  limit: z.number().int().min(1).max(200).default(10),     // how many frames to run
  pool: z.number().int().min(1).max(20000).default(1000),  // random-sample from the last N (random mode)
  category: z.enum(['giftcards', 'coupons']).default('giftcards'),
  run_label: z.string().trim().max(64).default(''),        // groups this run in the log
  // Which pool to read (self = this worker's own DB/R2; prod = read-only prod
  // bindings, v2 only). `order` + `offset` enable deterministic pagination over
  // the earliest/latest frames (for a full sweep, e.g. the first 1000); when a
  // non-zero offset is given, frames are taken in `order` from `offset` (NOT
  // randomly sampled).
  source: z.enum(['self', 'prod']).default('self'),
  order: z.enum(['asc', 'desc']).default('desc'),
  offset: z.number().int().min(0).default(0),
  paged: z.boolean().default(false),                       // true → ordered+offset slice, not random
  concurrency: z.number().int().min(1).max(12).default(3),
  // Inline-samples mode: run the eval over images supplied in the request (base64)
  // instead of sampling this worker's own pool. Lets a v2 backtest measure real
  // PROD images (read-only elsewhere) WITHOUT touching the live prod worker.
  samples: z.array(z.object({
    id: z.string().max(80).default(''),
    mime: z.string().max(100).default('image/jpeg'),
    fileBase64: z.string().min(1).max(9_000_000),
  })).max(50).default([]),
});
export type WorkEvalBacktestReq = z.infer<typeof WorkEvalBacktestReqSchema>;

export const WorkEvalBacktestFrameSchema = z.object({
  photo_id: z.string(),
  state: z.string(),
  reason: z.string(),
  confidence: z.number(),
  merchant: z.string(),
  lang: z.string(),
  lang_ok: z.boolean(),
  decoder_codes: z.number().int(),
  // Cascade (ADR-0030): which stage decided + the stage-2 scene facts (when the
  // frame was flagged). Defaulted so an older worker's response still parses.
  decided_by: z.string().default('stage1'),
  scene_model: z.string().default(''),
  card_count: z.number().int().default(-1),   // -1 = stage-2 not run
  on_store_rack: z.boolean().default(false),
  foreign_code: z.boolean().default(false),
  prompt_tokens: z.number().int(),
  output_tokens: z.number().int(),
  latency_ms: z.number().int(),
  error: z.string(),
});
export type WorkEvalBacktestFrame = z.infer<typeof WorkEvalBacktestFrameSchema>;

export const WorkEvalBacktestResSchema = z.object({
  run_label: z.string(),
  model: z.string(),
  prompt_version: z.string(),
  count: z.number().int(),
  frames: z.array(WorkEvalBacktestFrameSchema),
  by_state: z.record(z.string(), z.number().int()),        // {auto_passed: n, needs_qa: n, rejected: n}
  prompt_tokens: z.number().int(),
  output_tokens: z.number().int(),
  avg_latency_ms: z.number().int(),
  cost: z.object({
    in_usd_per_m: z.number(),      // assumed input price, $/1M tokens
    out_usd_per_m: z.number(),     // assumed output price, $/1M tokens
    usd_total: z.number(),         // for this run
    usd_per_frame: z.number(),     // usd_total / count
  }),
});
export type WorkEvalBacktestRes = z.infer<typeof WorkEvalBacktestResSchema>;
